/**
 * Console states (board B8) — what a page says when it has nothing to show.
 *
 * The empty state is the most-visited screen on this site and nobody designs
 * it, so it is worth being exact about the rules it follows:
 *
 *   1. State the count we do know. "Nothing found" is a shrug; "0 of 61 clear
 *      Strict" is a fact the reader can act on.
 *   2. Keep `below` and `unrated` apart. Seller coverage is around 6%, so most
 *      hidden listings are not below the floor -- we hold no numbers for them.
 *      Calling those "below your floor" is a claim about sellers we never
 *      judged, and it blames them for our gap.
 *   3. Offer one way out, with a real number on it. "Try a lower floor" is
 *      advice; "Lower to Standard - 23 clear" is a measured promise, and
 *      `clears_by_floor` on the list response is where the number comes from.
 *   4. Never invent. If a count is not in hand, the sentence does not appear.
 *
 * No spinner copy and no "Oops". Loading is the real grid in outline, so
 * nothing moves when data lands.
 *
 * DOM-free like console-cards.js: data in, string out, so the tests can run it
 * under Node and the callers stay in charge of when to draw.
 */

import { escapeHtml } from './console-cards.js';

/** Loosest to tightest, mirroring products/trust_floor.py::FLOORS. */
export const FLOOR_ORDER = ['off', 'standard', 'strict', 'elite'];

const FLOOR_NAMES = {
  off: 'Off', standard: 'Standard', strict: 'Strict', elite: 'Elite',
};

/** The floor one step looser, or null at the bottom. One step, never a menu. */
export function nextFloorDown(floor) {
  const index = FLOOR_ORDER.indexOf(floor);
  return index > 0 ? FLOOR_ORDER[index - 1] : null;
}

function floorName(floor) {
  return FLOOR_NAMES[floor] || String(floor || '');
}

/** A URL with one query parameter replaced, everything else left alone. */
function withParam(name, value, search = '') {
  const params = new URLSearchParams(search);
  params.set(name, value);
  return `?${params.toString()}`;
}

/**
 * "The floor hid everything" — the common empty state.
 *
 * `counts` is the `trust_floor` block off the list response:
 * {total, clears, below, unrated, clears_by_floor}.
 */
export function emptyFloorHtml(counts, { search = '' } = {}) {
  const floor = counts?.floor;
  const total = Number(counts?.total) || 0;
  const below = Number(counts?.below) || 0;
  const unrated = Number(counts?.unrated) || 0;

  // Two different sentences, and a page may need either, both or neither.
  const parts = [];
  if (below) parts.push(`${below} ${below === 1 ? 'is' : 'are'} below the floor`);
  if (unrated) {
    parts.push(`${unrated} come${unrated === 1 ? 's' : ''} from sellers we hold no rating for`);
  }
  const breakdown = parts.length
    ? `<p class="lede">Of ${total} listing${total === 1 ? '' : 's'}, ${parts.join(' and ')}.</p>`
    : '';

  const acts = [];
  if (total) {
    acts.push(`<a class="btn sm ghost" href="${escapeHtml(withParam('floor', 'off', search))}">Show all ${total}</a>`);
  }
  const lower = nextFloorDown(floor);
  const lowerClears = Number(counts?.clears_by_floor?.[lower]) || 0;
  // Only offered when it would actually change the page. A button that leads
  // to the same emptiness is worse than no button.
  if (lower && lowerClears > 0) {
    acts.push(
      `<a class="btn sm" href="${escapeHtml(withParam('floor', lower, search))}">`
      + `Lower to ${escapeHtml(floorName(lower))} · ${lowerClears} clear</a>`,
    );
  }

  return `<div class="panel empty state-floor">
    <p class="big">Nothing here clears ${escapeHtml(floorName(floor))}</p>
    ${breakdown}
    <p class="fnote">You can still look at the rest — they just won't carry a tier.</p>
    ${acts.length ? `<div class="acts">${acts.join('')}</div>` : ''}
  </div>`;
}

/**
 * "Nothing at all" — the floor is off and there is still nothing.
 *
 * Don't repeat the floor pitch; the floor is not the problem. Show what we
 * actually searched for, so the reader can see the typo, and offer to widen
 * the parts of the query that narrowed it.
 *
 * `intent` is the parsed query. Each repair is only offered when the query
 * carries the thing it would remove -- there is no "remove price range" on a
 * query with no price in it.
 */
export function emptyQueryHtml(intent, { search = '' } = {}) {
  const keyword = (intent?.query || '').trim();
  const bits = [];
  if (keyword) bits.push(`keyword <b>${escapeHtml(keyword)}</b>`);
  const min = intent?.minPrice;
  const max = intent?.maxPrice;
  if (min != null && max != null) bits.push(`between <b>$${escapeHtml(String(min))}</b> and <b>$${escapeHtml(String(max))}</b>`);
  else if (max != null) bits.push(`under <b>$${escapeHtml(String(max))}</b>`);
  else if (min != null) bits.push(`over <b>$${escapeHtml(String(min))}</b>`);
  const condition = intent?.condition;
  if (condition && condition !== 'all') bits.push(`condition <b>${escapeHtml(condition)}</b>`);

  const acts = [];
  if (min != null || max != null) {
    const params = new URLSearchParams(search);
    params.delete('min_price');
    params.delete('max_price');
    acts.push(`<a class="btn sm ghost" href="?${params.toString()}">Remove the price range</a>`);
  }
  if (condition && condition !== 'all') {
    const params = new URLSearchParams(search);
    params.delete('condition');
    acts.push(`<a class="btn sm ghost" href="?${params.toString()}">Search all conditions</a>`);
  }

  return `<div class="panel empty state-query">
    <p class="big">Nothing matched${keyword ? ` ${escapeHtml(keyword)}` : ''}</p>
    ${bits.length ? `<div class="parsed">We searched ${bits.join(', ')}.</div>` : ''}
    ${acts.length ? `<div class="acts">${acts.join('')}</div>` : ''}
  </div>`;
}

/**
 * Skeleton tiles in the real grid.
 *
 * Same element count and the same tile proportions as the grid that replaces
 * them, so the page does not jump. The shimmer is switched off under
 * prefers-reduced-motion in states.css.
 */
export function skeletonGrid(count = 8) {
  // Mirrors `.tile` in components.css: a 96px square thumbnail beside a column
  // of lines, same padding, same radius. The point of a skeleton is that the
  // page does not move when the data lands, so a shape that merely suggests
  // "a card" is worse than none -- it guarantees the jump it exists to avoid.
  const tile = '<div class="skel" aria-hidden="true">'
    + '<span class="ph"></span>'
    + '<span class="b"><span class="ln"></span><span class="ln s"></span>'
    + '<span class="ln p"></span></span>'
    + '</div>';
  return tile.repeat(Math.max(1, Math.min(24, count)));
}

/** How long ago, in the plainest words that are still accurate. */
export function agoLabel(since, now = Date.now()) {
  // `Number(null)` is 0, not NaN, so a missing timestamp would sail through
  // the finite check and report the age of the epoch.
  if (since === null || since === undefined || since === '') return '';
  const then = since instanceof Date ? since.getTime() : Number(since);
  if (!Number.isFinite(then)) return '';
  // Floored, not rounded: 30 seconds ago is not "1 minute ago".
  const minutes = Math.max(0, Math.floor((now - then) / 60000));
  if (minutes < 1) return 'a moment ago';
  if (minutes === 1) return '1 minute ago';
  if (minutes < 60) return `${minutes} minutes ago`;
  const hours = Math.round(minutes / 60);
  return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
}

/**
 * Upstream failed.
 *
 * When there are last-good results they stay on screen underneath, greyed, and
 * this sits above them saying how old they are. Stale listings with a
 * timestamp beat a blank page; stale listings without one do not.
 */
export function staleBannerHtml({ since = null } = {}) {
  const ago = agoLabel(since);
  return `<div class="banner" role="status">
    <span class="ic" aria-hidden="true">!</span>
    <span class="t">
      <b>Couldn't reach eBay just now</b>
      <span>${ago ? `Showing listings from ${escapeHtml(ago)}. Prices and availability may have changed.`
    : 'Prices and availability may have changed.'}</span>
    </span>
    <button type="button" class="btn sm ghost" data-retry>Retry</button>
  </div>`;
}

/**
 * One control failed. Anchored to it, and it keeps what the person typed --
 * making someone retype a price because our save failed is the site charging
 * them for its own problem.
 */
export function inlineErrorHtml(message, detail = '') {
  return `<div class="inline-err" role="alert">
    <span>${escapeHtml(message)}${detail ? ` <span class="mute">${escapeHtml(detail)}</span>` : ''}</span>
    <button type="button" data-retry>Retry</button>
  </div>`;
}

/**
 * Guest searches used up. Replaces the grid rather than sitting under it: at
 * zero remaining there is nothing under it to sit on.
 */
export function quotaHtml({ used = 0, limit = 0, resetMinutes = null } = {}) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 100;
  return `<div class="panel empty quota">
    <span class="n">${used} of ${limit}</span>
    <p class="lede">Guest searches used this hour</p>
    <div class="bar"><i style="width:${pct}%"></i></div>
    ${resetMinutes != null
      ? `<p class="fnote">Refreshes in ${Math.max(1, Math.round(resetMinutes))} min.</p>` : ''}
    <p class="fnote">A free account removes the limit, and saves your floor and your watchlist.</p>
    <div class="acts"><a class="btn sm" href="/register.html">Create a free account</a></div>
  </div>`;
}
